import { rem } from 'polished'
import PropTypes from 'prop-types'
import React from 'react'
import { formatPrice } from 'shared-constants'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import { Delimiter } from 'components/Delimiter'
import { GiftCardPreview } from 'components/GiftCardPreview'
import { Flex } from 'components/Layout'
import { Text, H5 } from 'components/Typography'
import { WrappedLoader } from 'components/WrappedLoader'
import { CURRENCY_ISO_CODE } from 'constants/common'
import { useCurrency } from 'providers/currency'
import { COLOR, FONT_WEIGHT, SPACE } from 'Theme'
import { SummaryWrapper } from '../components/SummaryWrapper'

const SECTION_MARGIN = SPACE.PX_20

export const CouponSummary = ({
  couponSummary,
  isLoading,
  isDiscountEnabled,
}) => {
  const selectedCurrency = useCurrency()
  const {
    currencyIsoCode,
    price,
    priceApprox,
    originalPrice,
    originalPriceApprox,
    discountPercentage,
  } = couponSummary

  return (
    <SummaryWrapper width={{ MOBILE: 'auto', TABLET: rem(360) }}>
      <Flex mb={SECTION_MARGIN} width="100%">
        <Flex flexDirection="column" justifyContent="center">
          <Text>Summary of your order</Text>
        </Flex>
      </Flex>
      <Delimiter />
      <Flex flex={1} width="100%" height="100%" my={SPACE.PX_15}>
        <GiftCardPreview isDiscount={isDiscountEnabled} />
      </Flex>
      <Delimiter />
      <Flex flexDirection="column" my={SECTION_MARGIN}>
        <H5 fontWeight={FONT_WEIGHT.NORMAL}>Description</H5>
        <Text>The Expert Gift Card</Text>
      </Flex>
      <Delimiter />
      <Flex flexDirection="column" mt={SECTION_MARGIN} position="relative">
        {isLoading && <WrappedLoader isOverlay minHeight="auto" />}
        <H5 fontWeight={FONT_WEIGHT.NORMAL}>Price details</H5>
        <Flex flexDirection="column" mb={SPACE.PX_10}>
          <Flex flex={1} justifyContent="space-between">
            <Text textTransform="capitalize">Gift Card</Text>
            <Text fontWeight={FONT_WEIGHT.MEDIUM}>
              {originalPrice &&
                formatPrice({
                  price: originalPrice,
                  currency: CURRENCY_ISO_CODE.USD,
                })}
            </Text>
          </Flex>
          <Text textAlign="right">
            {originalPriceApprox &&
              currencyIsoCode !== selectedCurrency &&
              `(approx. ${formatPrice({
                price: originalPriceApprox,
                currency: selectedCurrency.currencyIsoCode,
              })})`}
          </Text>
          {isDiscountEnabled && (
            <Flex
              data-test-class={DATA_TEST_CLASS.CHECKOUT_DISCOUNT_WRAPPER}
              my={SPACE.PX_10}
              flex={1}
              justifyContent="space-between"
            >
              <Text color={COLOR.SECONDARY_8} textTransform="capitalize">
                Discount
              </Text>
              <Text color={COLOR.SECONDARY_8} fontWeight={FONT_WEIGHT.MEDIUM}>
                -{discountPercentage}%
              </Text>
            </Flex>
          )}
        </Flex>
        <Delimiter />
        <Flex flex={1} justifyContent="space-between" mt={SPACE.PX_10}>
          <Text fontWeight={FONT_WEIGHT.MEDIUM} textTransform="capitalize">
            {`Total (${CURRENCY_ISO_CODE.USD})`}
          </Text>
          <Text fontWeight={FONT_WEIGHT.MEDIUM}>
            {price &&
              formatPrice({
                price,
                currency: CURRENCY_ISO_CODE.USD,
              })}
          </Text>
        </Flex>
        <Text textAlign="right">
          {priceApprox &&
            currencyIsoCode !== selectedCurrency &&
            `(approx. ${formatPrice({
              price: priceApprox,
              currency: selectedCurrency.currencyIsoCode,
            })})`}
        </Text>
      </Flex>
    </SummaryWrapper>
  )
}

CouponSummary.propTypes = {
  isDiscountEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  couponSummary: PropTypes.shape({
    currencyIsoCode: PropTypes.string,
    price: PropTypes.number,
    priceApprox: PropTypes.number,
    originalPrice: PropTypes.number,
    originalPriceApprox: PropTypes.number,
    discountPercentage: PropTypes.number,
  }),
}
